/**
 * The pricing engine — PRICING_RULES.md P-01 … P-10.
 *
 * This is the part of the product no competitor has. Every short-term rental system models price
 * as `nightly rate × nights − discount %`; none treats a monthly rent as a first-class object.
 * The whole engine is a pure function: no provider, no network, no clock.
 *
 * It never throws for a business refusal. A quote that may not be sent comes back as
 * `{ ok: false, refusals: [...] }` with every reason, because an agent needs all of them at once
 * rather than one per attempt. It throws only for programming errors — an unknown basis, a
 * malformed date.
 */

import { cents, prorate, sum, format, wholeDollarParts, toWholeDollar } from './money.js'
import { MONTHS, monthPercent, monthTierFor, leadTimeAdjustmentFor, applyAdjustments, describeStep } from './strategy.js'
import { splitIntoMonths, nightsBetween, coversACompleteMonth, isTwelveMonthsOrLonger, wholeMonthsBetween } from './stay.js'
import { UTILITY_BASIS, utilitiesSentence } from '../property/settings.js'
import { depositLawWarning } from './depositLaw.js'
import { capFor } from '../property/depositCap.js'

export const BASIS = {
  ACTUAL_CALENDAR_DAYS: 'ACTUAL_CALENDAR_DAYS',
  THIRTY_DAY: 'THIRTY_DAY_BASIS',
}

/** P-01: "Company default is THIRTY_DAY_BASIS." */
export const DEFAULT_BASIS = BASIS.THIRTY_DAY

/**
 * P-02: mid-term rental is 30+ nights. Machine-checked so the definition is enforced, not assumed.
 * The number is the company's setting (`availability/midTermMinimum.js`), re-exported live.
 */
import { MID_TERM_MINIMUM_NIGHTS as MINIMUM_NIGHTS } from '../availability/midTermMinimum.js'
export { MINIMUM_NIGHTS }

/**
 * Cleaning is not a one-time fee on a mid-term stay.
 *
 * P-05 calls the cleaning fee "one-time", which is true of a weekend. Told by the Product Owner
 * on 2026-09-04, it is not true of this business:
 *
 *   "打扫可能还有几种形式嘛。比如说是每个月扫一次的价格,或者是只有 checkout 扫一次的价格,
 *    对吧?那或者是双周扫一次的价格 — 那我们都可以报这种价格给到他的。"
 *
 * The size of the mistake is the point. The portfolio's median cleaning fee is $129. On a
 * twelve-month lease that is $129 quoted once, or $1,677 quoted monthly — a difference larger
 * than a month's rent on half the portfolio, produced by a field that looked settled.
 *
 * So a cleaning fee has a SCHEDULE as well as an amount, and the schedule is quoted explicitly.
 * P-05 still holds on the amount: whichever schedule is chosen, the fee per visit may not be
 * waived.
 */
export const CLEANING_SCHEDULES = [
  {
    id: 'CHECKOUT',
    label: 'Once, at checkout',
    describe: () => 'one clean, at the end of the stay',
    /** The whole stay, cleaned once when they leave. */
    occurrences: () => 1,
  },
  {
    id: 'MONTHLY',
    label: 'Monthly',
    describe: (n) => `one clean per calendar month the stay touches — ${n} in this case`,
    /**
     * One per calendar month the stay touches.
     *
     * Counted from the STAY, never from the priced segments — a defect found by the test below it.
     * A twelve-month lease prices as a single "annual fixed rate" row under P-08, so counting
     * segments gave one clean for a year and the quote was $1,419 short. How rent happens to be
     * priced has nothing to do with how often somebody cleans the house.
     *
     * Deliberately not "nights / 30" either: 18 Sep to 22 Dec touches four calendar months and
     * gets four cleans, where nights/30 says three and quietly drops one.
     */
    occurrences: ({ months }) => months,
  },
  {
    id: 'BIWEEKLY',
    label: 'Every two weeks',
    describe: (n) => `a clean every 14 nights plus one at checkout — ${n} in this case`,
    occurrences: ({ nights }) => Math.floor((nights - 1) / 14) + 1,
  },
  /*
    WEEKLY — Property setup has always had a weekly price box, and nothing could charge it: this
    list stopped at every two weeks, so a weekly price typed in by a person was never quoted.
    Found writing the Listing page, where the Product Owner asked for all four frequencies
    (2026-09-25). Same arithmetic as every two weeks, at seven nights.
  */
  {
    id: 'WEEKLY',
    label: 'Weekly',
    describe: (n) => `a clean every 7 nights plus one at checkout — ${n} in this case`,
    occurrences: ({ nights }) => Math.floor((nights - 1) / 7) + 1,
  },
]

export const DEFAULT_CLEANING_SCHEDULE = 'CHECKOUT'

/**
 * UTILITIES, AND THE TWO DIFFERENT PROMISES A QUOTE CAN MAKE ABOUT THEM.
 *
 * Neither of these is a fee in the P-05 sense. A cleaning fee recovers a cost this business
 * incurs and may not be waived; utilities are a bill somebody pays either way, and the only
 * question a quote answers is WHICH somebody. That is why this is its own line rather than
 * another entry in `oneTimeFees`: routed through there, a home with no utilities figure would
 * trip the waived-fee refusal for a fee that does not exist.
 *
 *   ON_TOP    — the renter pays the bills. The quote names the usual monthly figure so they can
 *               add it up, and it joins the total for every calendar month the stay touches.
 *   INCLUDED  — the rent covers them, UP TO A CAP. Nothing is added to the total, because the
 *               rent already carries it, and the cap is printed on the renter's own copy.
 *
 * THE CAP IS REQUIRED FOR "INCLUDED", AND THE REFUSAL IS THE POINT. Utilities are the one cost in
 * this business that the person being quoted controls and nobody else can. "Included" with no
 * ceiling is an open cheque written against somebody else's thermostat. The Product Owner asked
 * for the cap for a reason that is better than the accounting one: printed on the renter's copy,
 * it is the sentence that makes somebody close a window in February.
 */
function utilitiesLine(utilities, basis, months, refusals) {
  if (!basis) return null
  /* Property setup holds dollars, like every other money field there; a priced option holds cents. */
  const has = utilities?.monthly != null
  if (basis === UTILITY_BASIS.INCLUDED) {
    /*
      ONLY THE CAP IS NEEDED (the Product Owner, 2026-10-19: included has a ceiling, and we type it ourselves —
      on the quote, saved to the home). What the home usually costs is not part of this promise.
    */
    if (utilities?.cap == null) {
      refusals.push({
        rule: 'P-14',
        code: 'UTILITIES_NO_CAP',
        message: 'Included utilities need a monthly cap — type it under Utilities.',
      })
      return null
    }
    return {
      basis, months,
      covers: utilities.covers ?? [],
      description: utilitiesSentence(utilities.covers ?? []),
      monthlyCents: has ? cents(utilities.monthly) : null,
      capCents: cents(utilities.cap),
      /* Nothing joins the total. The rent is already carrying it. */
      amountCents: 0,
    }
  }
  /* Billed on top with no amount is a promise of a second number nobody gave (2026-10-19: no silent option). */
  if (!has) {
    refusals.push({
      rule: 'P-14',
      code: 'UTILITIES_NOT_SET',
      message: 'Billed on top needs a monthly amount — type it under Utilities.',
    })
    return null
  }
  const monthlyCents = cents(utilities.monthly)
  return {
    basis: UTILITY_BASIS.ON_TOP, months,
    covers: utilities.covers ?? [],
    description: utilitiesSentence(utilities.covers ?? []),
    monthlyCents,
    capCents: null,
    /*
      Counted by CALENDAR MONTHS TOUCHED, the same count a monthly clean uses and for the same
      reason: a utility company bills a month because the month happened, not because the rent was
      priced in thirty-night blocks. A stay from 18 September to 22 December is billed four times.
    */
    amountCents: monthlyCents * months,
  }
}

export const cleaningSchedule = (id) =>
  CLEANING_SCHEDULES.find((s) => s.id === id) ?? CLEANING_SCHEDULES[0]

/**
 * P-12 and P-13 now live on a PRICING STRATEGY, not on a property — see ./strategy.js.
 *
 * The reason is the Product Owner's: there are 126 homes and about four ways this business prices.
 * Per-property tiers meant typing the same policy 126 times and being unable to change it once,
 * so a rethink of the Seattle ladder would have been 95 edits and would never have happened.
 */

/**
 * One month of a stay, priced.
 *
 * P-09: a complete calendar month is charged the full monthly rule price — February is not cheaper
 * for having 28 days, and a 31-day month is not dearer. The basis affects partial months only.
 */
function priceSegment(segment, monthlyCents, minimumCents, basis, percent = 0) {
  // THE MONTH IS THE UNIT. The strategy holds twelve percentages, one per calendar month, and this
  // month of the stay takes its own. August at +50% on a $5,000 base is $7,500 and September at
  // +10% is $5,500 — two numbers, both on a price list, neither of them averaged from anything.
  //
  // A single "monthly rent" figure could never say this, which is why the adjustment is policy on
  // a shared strategy rather than a field on a property.
  //
  // THE MONTH ADJUSTMENT MOVES THE FLOOR TOO, AND THAT IS A DECISION.
  //
  // A home's minimum is expressed against its base rent. The adjustment RESTATES that base rent for
  // a month — November is simply worth less — so the floor for November moves with it. Leaving the
  // floor fixed would mean a −10% November quietly cancelled itself on every home whose minimum
  // sits near its rent, and the failure is invisible: quotes just start refusing.
  //
  // The other reading is defensible and it is the owner's: "I will not let this home for less than
  // $X a month, whatever month it is." Under that reading a −30% winter would drop the floor 30%
  // and the floor stops protecting anything. It is a Product Owner decision, it is written here
  // rather than buried, and the length and lead-time discounts still measure against the result.
  const adjusted = percent === 0 ? monthlyCents : Math.round(monthlyCents * (1 + percent / 100))
  const adjustedFloor = percent === 0 ? minimumCents : Math.round(minimumCents * (1 + percent / 100))
  const note = percent === 0 ? '' : ` · ${MONTHS[segment.month0]} ${percent > 0 ? '+' : ''}${percent}%`
  if (segment.isCompleteMonth) {
    return { ...segment, monthPercent: percent, basisUsed: `complete month${note}`, rentCents: adjusted, minimumCents: adjustedFloor }
  }
  const denominator = basis === BASIS.ACTUAL_CALENDAR_DAYS ? segment.daysInMonth : 30
  return {
    ...segment,
    monthPercent: percent,
    basisUsed:
      (basis === BASIS.ACTUAL_CALENDAR_DAYS ? `${segment.daysInMonth}-day month` : '30-day basis') + note,
    rentCents: prorate(adjusted, segment.occupiedNights, denominator),
    minimumCents: prorate(adjustedFloor, segment.occupiedNights, denominator),
  }
}

/** Only cleaning recurs. A parking or admin fee is charged once whatever the schedule says. */
const recurringCandidate = (f) => f.recurring === true || /clean/i.test(f.label ?? '')

/**
 * Spread a whole-stay total back across the months, exactly.
 *
 * Proportional to each month's own figure, so a cheap month stays cheap and an expensive one stays
 * expensive; the remainder lands on the last month, which is what makes the sum exact rather than
 * out by a cent or two. A quote that is out by a cent is a quote somebody has to explain.
 */
function spreadToSegments(segments, targetCents) {
  const total = sum(segments.map((s) => s.rentCents))
  if (segments.length === 0) return []
  if (total === targetCents || total === 0) {
    return segments.map((s) => ({ label: s.label, nights: s.occupiedNights, rentCents: s.rentCents }))
  }
  const out = segments.map((s) => ({
    label: s.label,
    nights: s.occupiedNights,
    rentCents: Math.round((s.rentCents / total) * targetCents),
  }))
  const drift = targetCents - sum(out.map((s) => s.rentCents))
  out[out.length - 1].rentCents += drift
  return out
}

/**
 * Is there an amount here at all?
 *
 * `cents()` THROWS on anything that is not a number, and it is right to — a malformed amount is a
 * programming error and must not be rounded into a price. An ABSENT amount is a different thing
 * entirely: Guesty holds no monthly rent, only a nightly `basePrice` and a `monthlyPriceFactor`,
 * and plenty of live listings have neither. `seedMonthlyRent` says so by returning null, honestly.
 *
 * So the engine asks this question BEFORE it asks `cents()` anything, and answers a missing rent
 * with a refusal instead of an exception.
 */
const isAmount = (v) => {
  if (typeof v === 'number') return Number.isFinite(v)
  return typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))
}

/**
 * The shape of an option that cannot be priced at all.
 *
 * Every key the priced shape has, because no consumer asks first — `QuoteReview` maps `segments`,
 * reads `fees` and formats `totalCents` on every option it is handed, and an object missing half
 * of them white-screens exactly as loudly as the exception this replaces.
 *
 * What differs is that every money figure is NULL rather than 0. A zero is a price, it prints as
 * `$0.00`, and a screenful of them is a quote somebody could send. Null is the absence of one, and
 * `priced: false` says the same thing in a field a screen can branch on without inferring it from
 * a figure.
 */
function unpriceable({ unit, refusals, checkIn, checkOut, nights, wholeMonth, basis, strategy, cleaning, monthsForTier }) {
  const schedule = cleaningSchedule(cleaning)
  return {
    ok: false,
    refusals,
    /*
      WARNINGS ARE NOT REFUSALS, AND THIS SHAPE CARRIES THEM TOO. (mtr#112.)

      A refusal stops the quote; a warning is something somebody has to be TOLD and may still
      decide to send. `ok` is computed from refusals alone, deliberately. An unpriceable option
      raises none — there is no figure yet to be lawful or unlawful about — but the key exists,
      because no consumer asks first and a screen mapping `warnings` over half its options is the
      same white screen this shape was written to prevent.
    */
    warnings: [],
    priced: false,
    unit: { id: unit.id, name: unit.name },
    renterSegments: [],
    stay: { checkIn, checkOut, nights, coversACompleteMonth: wholeMonth },
    basis,
    strategy: strategy ? { id: strategy.id, name: strategy.name } : null,
    listRentCents: null,
    monthsAdjusted: [],
    adjustments: [],
    discounts: [],
    wholeMonths: monthsForTier,
    lengthDiscount: null,
    leadTimeAdjustment: null,
    segments: [],
    rentCents: null,
    quotedRentCents: null,
    rentWasOverridden: false,
    minimumTotalCents: null,
    depositCents: null,
    depositWasOverridden: false,
    fees: [],
    feesCents: null,
    /*
      Present and empty, like every other figure on this shape. A screen reading an unpriceable
      option must find the same keys it finds on a priced one — the test beneath this file exists
      because a missing key renders as `undefined` on a page rather than as an error anybody sees.
    */
    utilities: null,
    utilitiesCents: null,
    cleaning: { scheduleId: schedule.id, label: schedule.label, description: null },
    totalCents: null,
    impliedNightlyCents: null,
  }
}

/**
 * Price one unit option over one stay.
 *
 * `unit` = { id, name, monthly, minimum, annual?, deposit?, oneTimeFees?: [{ label, amount }] }
 * Amounts are in dollars; everything inside is integer cents.
 */
export function priceOption(unit, {
  checkIn,
  checkOut,
  basis = DEFAULT_BASIS,
  rentOverride = null,
  /**
   * AN AGENT'S FIGURE FOR ONE MONTH (the Product Owner, 2026-10-19: 「每一个月份都可以单独改…这样有最好的弹性」).
   * `{ 'Dec 2026': 4900 }` in dollars, keyed by the month's label: what the renter pays THAT month, after
   * everything else. The rent is then the sum of the months, never a figure typed for the whole stay.
   */
  monthEdits = null,
  /** Which cleaning schedule this quote is offering. See CLEANING_SCHEDULES. */
  cleaning = DEFAULT_CLEANING_SCHEDULE,
  depositOverride = null,
  /** The shared pricing strategy this home is on, if any. See ./strategy.js. */
  strategy = null,
  /**
   * Which promise this quote makes about utilities — see `utilitiesLine`. Null means the quote
   * says nothing about them at all, which is what every quote did before this existed.
   */
  utilitiesBasis = null,
  /** When the quote is being made — required for a last-minute discount, never read from a clock. */
  asOf = null,
} = {}) {
  if (!Object.values(BASIS).includes(basis)) throw new TypeError(`unknown basis: ${basis}`)

  const nights = nightsBetween(checkIn, checkOut)
  const refusals = []
  const warnings = []

  // ---- P-02: the stay must qualify as mid-term at all -------------------------------------
  //
  // The second clause is the one found by running the test table rather than reading it: a stay
  // covering all of February is 28 nights, so a bare 30-night floor would reject a renter booking
  // exactly one calendar month -- as ordinary a mid-term booking as exists.
  const wholeMonth = coversACompleteMonth(checkIn, checkOut)
  if (nights < MINIMUM_NIGHTS && !wholeMonth) {
    refusals.push({
      rule: 'P-02',
      code: 'TOO_SHORT',
      message: `${nights} nights, and the stay covers no complete calendar month. Mid-term rental is ${MINIMUM_NIGHTS}+ nights.`,
    })
  }

  // ---- P-08: twelve months or longer uses the annual rate ---------------------------------
  const annual = isTwelveMonthsOrLonger(checkIn, checkOut)
  if (annual && unit.annual == null) {
    refusals.push({
      rule: 'P-08',
      code: 'NO_ANNUAL_RATE',
      message: 'Twelve months or longer uses the fixed annual rate, and this unit has none configured.',
    })
  }

  // Whole months, by anniversary — NOT the count of calendar months touched. A 35-night stay lasts
  // one month and touches two, and the tiers are read against how long it lasts.
  const monthsForTier = wholeMonthsBetween(checkIn, checkOut)

  // ---- P-01: there has to be a rent before there can be a price ---------------------------
  //
  // This was an exception, and it took the whole application down with it. A live Guesty row with
  // no `basePrice` seeds no monthly rent, `cents(null)` threw `not an amount: null`, and an agent
  // who added such a home to a quote got a white screen naming neither the home nor the reason.
  //
  // A home nobody has priced is not a programming error. It is the most ordinary business refusal
  // in this product, and this function already has the mechanism for one — so it goes in
  // `refusals` beside the others, and the agent is told all of them at once and told where to fix
  // this one.
  //
  // THE FLOOR COUNTS TOO, AND IS NOT INVENTED. A home with a rent but no minimum is refused on the
  // same line rather than quietly floored at zero: 90% is `seedMinimum`'s guess for a home nobody
  // has set up, not a policy the engine may apply on a rent a person actually typed.
  //
  // IT RETURNS HERE rather than carrying on with a zero. Every figure below this point is computed
  // from the rent, and a quote reading $0.00 rent, $0.00 deposit, $0.00 total is a price — a wrong
  // one, on a screen with a Send button. Twelve months or longer is refused with everything else
  // even where `unit.annual` is set, because P-07's deposit is one month's rent and there is none.
  if (!isAmount(unit.monthly) || !isAmount(unit.minimum)) {
    const missing = !isAmount(unit.monthly) ? 'monthly rent' : 'minimum rent'
    refusals.push({
      rule: 'P-01',
      code: 'NO_RENT',
      message: `${unit.name ?? 'This home'} has no ${missing} set, so it cannot be priced. Set one on the home in Property setup — Guesty carries no monthly rent, and nothing could be derived for this listing.`,
    })
    return unpriceable({ unit, refusals, checkIn, checkOut, nights, wholeMonth, basis, strategy, cleaning, monthsForTier })
  }

  const monthlyCents = cents(unit.monthly)
  const minimumCents = cents(unit.minimum)

  let segments = []
  let rentCents
  let minimumTotalCents

  if (annual && unit.annual != null) {
    // P-08: "Monthly rule prices do not apply and are not compared against it."
    rentCents = cents(unit.annual)
    minimumTotalCents = 0
    segments = [{
      label: 'Annual fixed rate',
      occupiedNights: nights,
      basisUsed: 'annual (P-08)',
      rentCents,
      minimumCents: 0,
      isCompleteMonth: false,
    }]
  } else {
    segments = splitIntoMonths(checkIn, checkOut).map((s) =>
      priceSegment(s, monthlyCents, minimumCents, basis, strategy ? monthPercent(strategy, s.month0) : 0)
    )
    // Rounding order matters: the total is the sum of ROUNDED segments, so the printed lines add
    // up to the printed total.
    rentCents = sum(segments.map((s) => s.rentCents))
    minimumTotalCents = sum(segments.map((s) => s.minimumCents))
  }

  // ---- P-12 / P-13: the strategy's whole-stay adjustments, in a fixed order, each reported ------
  //
  // P-08 shuts both off. Twelve months or longer is a fixed annual rate — a number a human agreed
  // with an owner — and a strategy that quietly took 7% off it would be overriding that agreement.
  const monthTier = annual ? null : monthTierFor(strategy, monthsForTier)
  const leadTime = annual ? null : leadTimeAdjustmentFor(strategy, { checkIn, asOf })
  const listRentCents = rentCents
  const adjusted = applyAdjustments(rentCents, { monthTier, leadTime })
  rentCents = adjusted.cents

  // ---- An agent's rent edit, and the floor it may not cross -------------------------------
  let quotedRentCents = rentCents
  if (rentOverride != null) {
    quotedRentCents = cents(rentOverride)
    // P-05: the minimum constrains RENT ONLY. One-time fees are excluded from the comparison.
    if (!annual && quotedRentCents < minimumTotalCents) {
      refusals.push({
        rule: 'P-02',
        code: 'BELOW_MINIMUM',
        message: `${format(quotedRentCents)} is below the combined minimum of ${format(minimumTotalCents)}.`,
      })
    }
  } else if (adjusted.steps.length > 0 && !annual && quotedRentCents < minimumTotalCents) {
    // A CONFIGURED discount can cross the minimum without anybody touching a field, and that is
    // the dangerous version: an agent's override is a decision somebody made on the day, while
    // this one was set once on a shared strategy and applies silently to every home using it.
    refusals.push({
      rule: 'P-12',
      code: 'DISCOUNT_BELOW_MINIMUM',
      message: `${adjusted.steps.map(describeStep).join(' and ')} brings rent to ${format(quotedRentCents)}, below this home's minimum of ${format(minimumTotalCents)}. The strategy and the home disagree.`,
    })
  }

  /*
    THE MONTHS AS SHE PAYS THEM, AND ANY A PERSON CHANGED.

    A month typed on Build Quote replaces that month's figure and nothing else; the rent is their sum.
    Each typed month is held to that month's own minimum (the prorated one for a part month), so a
    discount on one month cannot hide inside a total that still clears the floor.
  */
  let renterMonths = spreadToSegments(segments, quotedRentCents)

  /*
    WHOLE DOLLARS FROM HERE ON (the Product Owner, 2026-10-19/20: 「租金从报价起就是整数」; part months:
    「总数，四舍五入」). The stay's rent is its total rounded to the dollar; each month is rounded to the dollar
    and the last month takes what rounding left, so the months add up to the Rent line exactly. It is the
    lease's own rounding (`wholeDollarParts`, money.js) — the lease's payment schedule reads these months
    and must arrive at the same figures, never a dollar apart.

    THE COMPUTED MONTHS ARE SETTLED BEFORE ANY TYPED MONTH IS APPLIED (Copilot, #690). Settling after the
    edits let the last month's remainder land on a month a person typed: $4,450 at −7% every month,
    Nov 1 2027 → Feb 1 2028 shows $4,139 / $4,139 / $4,138, and typing January's own $4,138 back in made it
    $4,137. A typed month is the person's decision and is charged as typed (to the dollar); the months
    nobody typed keep exactly the figures they were shown, and the rent is the sum.

    Each computed month is held to its own minimum through the rounding too (money.js), including one the
    proportional spread left a cent or so under it — that shortfall is the allocation's, not a price.

    `rentCents`, `listRentCents` and the strategy's steps above stay as computed: they are the staff's
    working, not what she is charged. `quotedRentCents` and these months are.
  */
  const floors = annual ? [] : renterMonths.map((_, i) => segments[i]?.minimumCents ?? null)
  const computedExactCents = quotedRentCents
  const spreadCents = renterMonths.map((m) => m.rentCents)
  const settled = wholeDollarParts(renterMonths.map((m) => m.rentCents), quotedRentCents, floors, { nearFloorCents: 99 })
  renterMonths = renterMonths.map((m, i) => ({ ...m, rentCents: settled.parts[i] }))
  quotedRentCents = settled.totalCents
  /*
    What the months a person CHANGED contribute to the deposit's ceiling below, against what they contributed
    before. Only a month whose charged figure differs from the one shown counts: see the no-op just below.
  */
  let changedChargedCents = 0
  let changedComputedCents = 0

  const typedMonths = monthEdits && typeof monthEdits === 'object' ? monthEdits : {}
  let monthsEdited = 0
  renterMonths = renterMonths.map((m, i) => {
    const v = typedMonths[m.label]
    if (!isAmount(v)) return m
    const rentForMonth = cents(v)
    const floorForMonth = segments[i]?.minimumCents ?? 0
    /* To the dollar; a typed figure that met its minimum is not rounded under it (money.js). */
    const charged = wholeDollarParts([rentForMonth], rentForMonth, annual ? [] : [floorForMonth]).parts[0]
    /*
      A MONTH TYPED AS IT IS SHOWN IS NOT AN EDIT (Copilot, #690). Re-entering January's own $2,536 on
      $4,450 at −7%, Oct 20 2027 → Jan 20 2028, actual days, changed no charge — but it swapped January's
      unrounded share ($2,535.6…) for $2,536 in the deposit's ceiling, which sat on a half dollar
      ($4,138.50), so the ceiling fell $4,139 → $4,138 and a $4,139 deposit was reported "$1 over". When the
      figure she would be charged is the figure already shown, the month is left exactly as computed:
      same contribution to the rent, the ceiling and every warning, and no "edited" mark.
    */
    if (charged === m.rentCents) return m
    monthsEdited += 1
    if (!annual && rentForMonth < floorForMonth) {
      refusals.push({
        rule: 'P-02',
        code: 'BELOW_MINIMUM',
        message: `${m.label}: ${format(rentForMonth)} is below this month's minimum of ${format(floorForMonth)} — raise it to at least that.`,
      })
    }
    changedChargedCents += charged
    changedComputedCents += spreadCents[i]
    return { ...m, rentCents: charged, edited: true, computedCents: m.rentCents }
  })
  if (monthsEdited > 0) quotedRentCents = sum(renterMonths.map((m) => m.rentCents))

  /*
    THE RENT THE DEPOSIT'S CEILING IS SCALED BY, read from the CHARGED months (Copilot, #690). A month charged
    what the quote computed — typed or not — contributes its share before rounding, as it always did; a month
    charged something else contributes exactly what it is charged (whole dollars), never the unrounded figure
    somebody typed. So the ceiling is a function of the months she pays and nothing else: typing a month's
    shown figure, or a figure that rounds to it, cannot move it.
  */
  const exactQuotedRentCents = computedExactCents - changedComputedCents + changedChargedCents

  // ---- P-03 / P-05 / P-07: what the total contains ----------------------------------------
  const schedule = cleaningSchedule(cleaning)
  // The stay's own calendar months, independent of how the rent was priced.
  const monthsTouched = splitIntoMonths(checkIn, checkOut).length
  const fees = (unit.oneTimeFees ?? []).map((f) => {
    // P-05: "One-time fees ... may not be waived." A zero or absent amount is a waiver by another
    // name, and it must be refused where an agent could have caused it.
    /*
      WHICH FIGURE IS CHARGED is decided first, so the waiver check reads that figure — not Guesty's
      amount on a line whose price comes from Property setup.
    */
    const configured = recurringCandidate(f) ? unit.cleaningPrices?.[schedule.id] : null
    const charged = configured != null ? configured : f.amount
    /*
      NO PRICE FOR THIS FREQUENCY, FROM EITHER SOURCE: refused, never borrowed. A home with only a
      Monthly price quoted "once, at checkout" must not be charged the Monthly figure. Found by review
      on #50, after a first fix that did exactly that.
    */
    if (charged == null) {
      refusals.push({
        rule: 'P-03',
        code: 'NO_CLEANING_PRICE',
        message: `No price for “${schedule.label}” cleaning on ${unit.name ?? 'this home'}. Set it in Property setup.`,
      })
    }
    /* Zero is a waiver and a negative price is a discount on the rent: both are refused (#50 review). */
    if (f.waived === true || (charged != null && cents(charged) <= 0)) {
      refusals.push({
        rule: 'P-05',
        code: 'FEE_WAIVED',
        message: `The ${f.label} fee may not be waived — fees recover cost and are never discounted.`,
      })
    }
    /*
      THE CLEANING PRICE COMES FROM PROPERTY SETUP, NOT FROM GUESTY.

      Property setup holds a price per frequency — a fortnightly visit is lighter work than a
      turnover and costs less — and the quote was ignoring all four of them and using Guesty's
      single `cleaningFee` for every schedule. So "monthly" and "once at checkout" were quoted at
      the same price per visit, which is a figure nobody in this business agreed to.

      `unit.cleaningPrices[schedule]` wins when the home has been set up. Guesty's fee is the
      fallback, and the option reports which one it used so the screen can say so rather than imply.
    */
    /*
      WHOLE DOLLARS (the Product Owner, 2026-10-02, #343: 「全部金额只收整数元」). Property setup only takes whole
      dollars, but Guesty's fee — the fallback — can carry cents ($129.50), and Confirm lease terms takes the
      cleaning from the quote and accepts whole dollars only. So the quote charges it to the dollar, the same
      rounding as the rent (money.js toWholeDollar), and the lease never refuses the quote's own number.
    */
    const perVisit = charged == null ? 0 : toWholeDollar(cents(charged))
    // Only cleaning recurs. A parking fee or an admin fee is charged once whatever the schedule
    // says, and multiplying it would invent money.
    const recurring = recurringCandidate(f)
    const occurrences = recurring ? schedule.occurrences({ months: monthsTouched, nights }) : 1
    return {
      label: f.label,
      unitAmountCents: perVisit,
      occurrences,
      recurring,
      amountCents: perVisit * occurrences,
      scheduleId: recurring ? schedule.id : null,
      /** 'property' when a person set this price, 'derived' when it came off the Guesty listing. */
      source: recurring ? (configured != null ? 'property' : 'derived') : 'derived',
    }
  })
  const feesCents = sum(fees.map((f) => f.amountCents))

  /*
    THE ONE PLACE THE LAW CAN ACTUALLY BE CHECKED. (mtr#112.) Everything the question needs exists
    here and nowhere earlier: the home, the stay, the fees being charged, and the figure itself —
    including an overridden one, which is the case a check on the policy could never see.

    THE FIRST MONTH IS THE ONE THE RENTER PAYS — AND ONLY WHEN IT IS A WHOLE MONTH.

    `renterSegments` is the stay as she is shown it, so a January at −10% shrinks the month the
    deposit is measured against, and that is the figure to use: it IS her first month's rent.

    A stay starting on the 12th is the trap, and it was in the first version of this. Its opening
    segment is nineteen prorated nights — $2,786.67 on a $4,400 home — and measuring a full
    month's deposit against a nineteen-day stub reports every ordinary mid-month move-in as
    thousands over the line. "First month's rent" in SMC 7.24.020 is the rent for a month, not the
    stub that happens to fall before the first of the next one. So a partial opening segment falls
    back to the home's monthly rent, and so does the annual basis, which has no monthly segments
    at all.
  */
  /*
    AND ON THE ANNUAL BASIS IT IS THE ANNUAL RATE DIVIDED BY TWELVE, NOT THE LIST MONTHLY RENT.
    (Copilot, mtr#112.) `unit.annual` is a figure a person agreed with an owner and P-08 says the
    monthly rules do not apply to it — so the two need not agree, and on this portfolio they do
    not: the Seattle home is $4,400 a month and $47,000 a year, which is $3,916.67. Measuring
    against the larger of the two lets a deposit $483 over the line pass silently, and an override
    of the annual rate moves it again. It is `quotedRentCents`, so the figure actually charged.
  */
  /*
    AND A PARTIAL OPENING MONTH STILL TAKES ITS MONTH'S ADJUSTMENT WITH IT. (Copilot, mtr#112.)

    Falling back to the bare `monthlyCents` threw away the very thing the complete-month branch
    exists to catch: a stay from Jan 12 with January at −10% charges $1,575 for a full January, and
    a $1,750 deposit is $175 over a ceiling this measured as $1,750.

    So the fallback is the FULL first month as this quote prices it — the home's monthly rent with
    the opening month's own percentage on it, then scaled by whatever the whole-stay adjustments
    and an agent's override did to the rent. The ratio is how a length-of-stay discount reaches a
    figure that is not any segment, and it is 1 when nothing moved.
  */
  /*
    AND IT IS ROUNDED THE WAY THE MONTH ITSELF IS ROUNDED. (Copilot, mtr#112.) `priceSegment`
    charges `Math.round(monthlyCents * (1 + percent / 100))`, so a ceiling computed from the
    unrounded product lands a cent below the figure actually charged: $1,750.01 at −1% charges
    $1,732.51 against a ceiling of $1,732.50, and a deposit of exactly the month is reported as a
    cent over. A warning that fires on a deposit equal to the rent is one nobody reads twice.
  */
  /* The ratio before the rent was written in whole dollars: a full month is not any month she is charged, so it is not rounded. */
  const wholeStayRatio = listRentCents > 0 ? exactQuotedRentCents / listRentCents : 1
  const adjustedFullMonthCents = Math.round(monthlyCents * (1 + (segments[0]?.monthPercent ?? 0) / 100))
  /*
    WHOLE DOLLARS, LIKE THE DEPOSIT IT IS MEASURED AGAINST (#343; 2026-10-21). Every month she is charged is
    whole dollars and so is the deposit, so a full first month is written the same way. Left to the cent,
    a deposit of exactly one month's rent — $1,750.01 at −1%, $1,732.51, charged as $1,733 — is reported as
    $0.49 over a ceiling of $1,732.51 that no month of hers ever costs.
  */
  const fullFirstMonthCents = toWholeDollar(Math.round(adjustedFullMonthCents * wholeStayRatio))
  /*
    AND NEVER ABOVE A COMPLETE MONTH SHE IS ACTUALLY CHARGED (design review 2026-10-21, finding 1). The full
    first month above is made up: no month on her quote is that figure. Written to the dollar, it can sit $1
    above the real month — $4,400.50 a month, Oct 22 → Dec 1 2027 on actual days: the made-up month is $4,401,
    but November, the last month, takes the rounding and is charged $4,400. A deposit of $4,401 then passed,
    $1 over the only full month she pays. So when the stay opens with a part month and has a complete one,
    the ceiling is the smaller of the made-up month and the FIRST complete month she is charged.

    The first, not the smallest: a stay from Oct 22 with January at −10% has its first month in October, and
    measuring a one-month deposit against January's discounted figure would warn on an ordinary quote.
  */
  const firstCompleteIndex = segments.findIndex((s) => s.isCompleteMonth)
  const firstCompleteChargedCents = firstCompleteIndex >= 0 ? renterMonths[firstCompleteIndex]?.rentCents : null
  const firstMonthRentCents = annual
    ? quotedRentCents / 12
    : segments[0]?.isCompleteMonth
      ? renterMonths[0]?.rentCents ?? fullFirstMonthCents
      : Number.isFinite(firstCompleteChargedCents)
        ? Math.min(fullFirstMonthCents, firstCompleteChargedCents)
        : fullFirstMonthCents
  /*
    ROUNDING NEVER TAKES A DEPOSIT OVER THE LINE (design review 2026-10-21, finding 1). The deposit is written
    in whole dollars, half away from zero, except where that would put it above the month it is measured
    against:
      · any deposit at or under that month before rounding is rounded DOWN instead;
      · a deposit of ONE MONTH'S RENT (the default, or a listing deposit equal to the monthly rent — not a figure
        typed on this quote) that is above it by less than a dollar is that month before its own rounding, so it
        is written as the month: $4,400.50 a month, November charged $4,400, deposit $4,400 — not $4,401.
    Neither changes a price decision. Any other deposit over the month — a listing's $1,733.50 against a $1,733
    month, or anything a person typed on this quote — is left as it is, and the warning below says so
    (depositLaw.js: it warns, it never lowers).
  */
  // P-07: deposit is one month's rent unless overridden — by the listing, or by an agent here.
  // Whole dollars, as every amount on the quote is (#343): the default (one month's rent, or the listing's
  // deposit) is rounded the rent's way, so Confirm lease terms — whole dollars only — takes it as it is.
  const depositExactCents = depositOverride != null ? cents(depositOverride) : unit.deposit != null ? cents(unit.deposit) : monthlyCents
  const depositRoundedCents = toWholeDollar(depositExactCents)
  const depositIsOneMonth = depositOverride == null && depositExactCents === monthlyCents
  /*
    ONLY WHERE A CEILING APPLIES (Copilot, #690). Both adjustments above exist because a city caps the deposit
    at the month; in a city with no cap there is no line to keep it under, and the deposit is rounded like
    any other amount. Bellevue, $4,400 a month, $52,799 a year, a 12-month quote: the month it is measured
    against is $4,399.92, and without this the $4,400 deposit was written down to $4,399.
  */
  const capApplies = capFor({ city: unit.city ?? null, monthlyRent: Number.isFinite(firstMonthRentCents) ? firstMonthRentCents / 100 : null }).kind === 'CAP'
  const roundedOverTheMonth = capApplies && Number.isFinite(firstMonthRentCents) && depositRoundedCents > firstMonthRentCents
  const depositCents = !roundedOverTheMonth
    ? depositRoundedCents
    : depositExactCents <= firstMonthRentCents
      ? Math.floor(depositExactCents / 100) * 100
      : depositIsOneMonth && depositExactCents - firstMonthRentCents < 100
        ? Math.floor(firstMonthRentCents / 100) * 100
        : depositRoundedCents

  const utilities = utilitiesLine(unit.utilities, utilitiesBasis, monthsTouched, refusals)
  const utilitiesCents = utilities?.amountCents ?? 0

  const totalCents = quotedRentCents + depositCents + feesCents + utilitiesCents

  const lawWarning = depositLawWarning({
    city: unit.city ?? null,
    firstMonthRentCents,
    depositCents,
    fees,
  })
  if (lawWarning) warnings.push(lawWarning)

  return {
    ok: refusals.length === 0,
    refusals,
    /*
      WHAT SOMEBODY MUST BE TOLD, WITHOUT BEING STOPPED. (mtr#112.) `ok` reads `refusals` only.
      A deposit over a city's limit belongs here and not there: the rule governs a residential
      tenancy and not every stay is one, so the person sending decides — but they decide knowing.
    */
    warnings,
    /** True whenever there was a rent to price from. See `unpriceable` for the other shape. */
    priced: true,
    unit: { id: unit.id, name: unit.name },
    /*
      THE MONTHS AS THE RENTER MUST SEE THEM, AND WHY THEY ARE NOT `segments`.

      `segments` are the LIST price of each month — the figure before the length-of-stay and
      lead-time adjustments, which apply to the whole stay rather than to any one month. They are
      exactly what an agent needs, because the adjustments are then shown as their own lines
      underneath and the arithmetic is legible.

      They are exactly wrong for the renter. Her copy shows the months and then a Rent line, with no
      discount lines between them, so the months summed to $7,113.70 above a Rent of $6,413.72 —
      $699.98 of difference, on the one document in this product with an audience outside the
      company. "A renter who adds them up finds no discrepancy" is a rule this product states out
      loud, and it was being broken on the only page where it matters.

      So the whole-stay adjustment is spread back across the months, and the rounding remainder goes
      on the last one so the sum is exact to the cent rather than nearly right.
    */
    renterSegments: renterMonths,
    stay: { checkIn, checkOut, nights, coversACompleteMonth: wholeMonth },
    basis: annual ? 'ANNUAL' : basis,
    /** Every adjustment step, in order. An unexplained number is not a price. */
    strategy: strategy ? { id: strategy.id, name: strategy.name } : null,
    listRentCents,
    /** Which months of the stay were adjusted, and by how much — the "why this price" list. */
    monthsAdjusted: segments
      .filter((s) => s.monthPercent)
      .map((s) => ({ label: s.label, percent: s.monthPercent })),
    adjustments: adjusted.steps,
    /** Kept under the old names so nothing downstream had to change on the same day. */
    discounts: adjusted.steps,
    wholeMonths: monthsForTier,
    lengthDiscount: monthTier ? { ...monthTier, monthsCounted: monthsForTier } : null,
    leadTimeAdjustment: leadTime,
    segments,
    rentCents,
    quotedRentCents,
    rentWasOverridden: rentOverride != null || monthsEdited > 0,
    minimumTotalCents,
    depositCents,
    depositWasOverridden: depositOverride != null,
    fees,
    feesCents,
    /*
      Null when this quote says nothing about utilities — which is not the same as "none" and not
      the same as "included". A screen that printed "Utilities: $0" for a home nobody has costed
      would be answering a question that has not been asked.
    */
    utilities,
    utilitiesCents,
    cleaning: {
      scheduleId: schedule.id,
      label: schedule.label,
      /** Written out so a quote can state what the renter is actually buying. */
      description: schedule.describe(schedule.occurrences({ months: monthsTouched, nights })),
    },
    totalCents,
    /** Shown on screen because it is the number a renter computes themselves. Never a price input. */
    impliedNightlyCents: Math.round(quotedRentCents / nights),
  }
}

/**
 * What the same option would cost on the other basis.
 *
 * P-01's guard: the choice moves the total by −1.3% to +3.3% on an identical 30-night stay. Showing
 * only the chosen figure would make a several-percent decision invisible — an unlogged discount
 * authority. Returns null where the comparison is meaningless (annual rate, or no partial months).
 */
export function basisComparison(unit, stay) {
  const chosen = stay.basis ?? DEFAULT_BASIS
  if (isTwelveMonthsOrLonger(stay.checkIn, stay.checkOut)) return null
  const other = chosen === BASIS.THIRTY_DAY ? BASIS.ACTUAL_CALENDAR_DAYS : BASIS.THIRTY_DAY
  const a = priceOption(unit, { ...stay, basis: chosen, rentOverride: null })
  const b = priceOption(unit, { ...stay, basis: other, rentOverride: null })
  /* The rent she would be charged on each basis — whole dollars, as the option's Rent line is (2026-10-21). */
  const chosenCents = a.quotedRentCents
  const otherCents = b.quotedRentCents
  if (chosenCents === otherCents) return null
  return {
    chosen,
    other,
    chosenRentCents: chosenCents,
    otherRentCents: otherCents,
    differenceCents: otherCents - chosenCents,
    differencePercent: ((otherCents - chosenCents) / chosenCents) * 100,
  }
}

/**
 * Price a whole quote: one to three options over one stay.
 *
 * P-04 and P-01 are enforced here rather than per option, because they are properties of the quote
 * — every option shares one date range and one basis.
 */
export function priceQuote({ options = [], checkIn, checkOut, basis = DEFAULT_BASIS, cleaning = DEFAULT_CLEANING_SCHEDULE, utilitiesBasis = null, asOf = null } = {}) {
  const refusals = []

  if (options.length === 0) refusals.push({ rule: 'P-04', code: 'NO_OPTIONS', message: 'A quote needs at least one option.' })
  if (options.length > 3) {
    refusals.push({
      rule: 'P-04',
      code: 'TOO_MANY_OPTIONS',
      message: `A quote carries at most three options; this has ${options.length}.`,
    })
  }

  // P-04: options share one date range. P-01: one basis per quote. Both are refused rather than
  // silently normalised -- an option quietly re-dated is a quote nobody can honour.
  for (const o of options) {
    if ((o.checkIn && o.checkIn !== checkIn) || (o.checkOut && o.checkOut !== checkOut)) {
      refusals.push({
        rule: 'P-04',
        code: 'MIXED_DATES',
        message: `${o.unit?.name ?? o.unit?.id ?? 'An option'} uses different dates. Every option on a quote shares one date range.`,
      })
    }
    if (o.basis && o.basis !== basis) {
      refusals.push({
        rule: 'P-01',
        code: 'MIXED_BASIS',
        message: `${o.unit?.name ?? o.unit?.id ?? 'An option'} uses a different basis. One basis per quote.`,
      })
    }
  }

  const priced = options.map((o) =>
    priceOption(o.unit, {
      checkIn,
      checkOut,
      basis,
      rentOverride: o.rentOverride ?? null,
      monthEdits: o.monthEdits ?? null,
      depositOverride: o.depositOverride ?? null,
      // One cleaning schedule per quote unless an option deliberately differs: three options a
      // renter compares must differ in the home, not in what the fee silently covers.
      cleaning: o.cleaning ?? cleaning,
      /*
        ONE ANSWER ABOUT UTILITIES PER QUOTE, for the same reason there is one cleaning schedule:
        three options a renter compares must differ in the HOME, not in what the price silently
        covers. An option may still override it — two homes where one owner insists on including
        them is a real case — and then the difference is on the screen rather than inside it.
      */
      utilitiesBasis: o.utilitiesBasis ?? utilitiesBasis,
      strategy: o.strategy ?? null,
      asOf,
    })
  )

  for (const p of priced) refusals.push(...p.refusals)
  /* Gathered the same way and kept apart the same way: `ok` never reads this list. (mtr#112.) */
  /*
    AND EACH ONE REMEMBERS WHICH HOME IT IS ABOUT. (Copilot, mtr#112.) A quote compares three
    homes; this list is rendered once, underneath all of them; and "the deposit on this quote is
    $1,250 over" names no home at all. An agent reading that has to price each option again by
    hand to find out which one it means.
  */
  const warnings = priced.flatMap((p) => (p.warnings ?? []).map((w) => ({ ...w, unit: p.unit })))

  return {
    ok: refusals.length === 0,
    refusals,
    warnings,
    stay: { checkIn, checkOut },
    basis,
    cleaning,
    utilitiesBasis,
    options: priced,
    /*
      PRICED THE WAY THE OPTION IS (found live on Build Quote, 2026-10-19): without the option's strategy and the
      quote's date, the two Proration figures left out the season and lead-time adjustments, and read $6,626.77
      beside a Rent of $6,348.87 for the same dates.
    */
    comparison: options.length
      ? basisComparison(options[0].unit, { checkIn, checkOut, basis, strategy: options[0].strategy ?? null, asOf })
      : null,
  }
}
